import React, { useState } from 'react'
import _ from 'lodash'
import axios from 'axios'
import { Button, Spinner } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import MyInput from '../../../../features/forms/MyInput'
import {
  createSecret,
  deleteSecret,
  updateSecret,
} from '../../../../features/aws/awsSecretsManagerSlice'
import {
  closeConnectionModal,
  createPublicKey,
  deletePublicKey,
  updatePublicKey,
  postSlackMessage,
} from '../../integrationsSlice'
import { toast } from 'react-toastify'
import { selectRootUser } from '../../../../rootSlice'
import { SLACK_DATA_STATUS_CHANNEL_ID } from '../../../../modules/constants'
import { integrationsKeysUrl } from '../../../../modules/urls'
import { getAxiosDefaultConfig } from '../../../../modules/utils'
import { IntercomAPI } from 'react-intercom'

export default function StripeConnectionModalContent({ ownerId, channel, retrieveAccounts }) {
  const dispatch = useDispatch()
  const closeModal = () => dispatch(closeConnectionModal())
  const user = useSelector(selectRootUser)
  const { id: channelId, channel: channelName, ppKeys } = channel || {}

  const [verifying, setVerifying] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm()

  const hasExistingKey = !!ppKeys && !_.isEmpty(ppKeys)

  const onSubmit = async (values) => {
    const { secretKey } = values
    if (!secretKey?.trim()) {
      toast.error('Secret key is required')
      return
    }

    // Step 1: Verify the key and get the Stripe account ID
    setVerifying(true)
    let stripeAccount
    try {
      const verifyRes = await axios.post(
        `${integrationsKeysUrl}/stripe-verify`,
        { secretKey },
        getAxiosDefaultConfig()
      )
      stripeAccount = verifyRes.data
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Invalid Stripe secret key'
      toast.error(msg)
      setVerifying(false)
      return
    }
    setVerifying(false)

    // Step 2: Save the key using the real Stripe account ID
    const accountId = stripeAccount.id // e.g. acct_xxx
    const fullKeyId = `${ownerId}--${channelId}--${accountId}`

    const existingAccountIds = (ppKeys || [])
      .map(({ keyId }) => {
        const parts = keyId?.split('--')
        return parts?.length === 3 ? parts[2] : null
      })
      .filter(Boolean)

    const tags = [
      { Key: 'ownerId', Value: ownerId },
      { Key: 'channelId', Value: channelId },
      { Key: 'accountId', Value: accountId },
      {
        Key: 'accountName',
        Value: stripeAccount.business_profile?.name || stripeAccount.email || accountId,
      },
    ]

    // Store the email as the public key value so the integrations table can display it
    const publicKeyValue = stripeAccount.email || accountId

    try {
      if (existingAccountIds.includes(accountId)) {
        await dispatch(updateSecret({ secretId: fullKeyId, secretValue: secretKey })).unwrap()
        await dispatch(updatePublicKey({ keyId: fullKeyId, keyValue: publicKeyValue, tags })).unwrap()
      } else {
        // Delete old keys if any (e.g. migrating from old format like "default")
        for (const oldKey of ppKeys || []) {
          if (oldKey.keyId) {
            try {
              await dispatch(deleteSecret({ secretId: oldKey.keyId })).unwrap()
              await dispatch(deletePublicKey({ keyId: oldKey.keyId })).unwrap()
            } catch {}
          }
        }

        await dispatch(
          createSecret({
            secretId: fullKeyId,
            secretValue: secretKey,
            description: `Stripe secret key for account ${accountId}`,
            tags,
          })
        ).unwrap()
        await dispatch(
          createPublicKey({
            keyId: fullKeyId,
            keyValue: publicKeyValue,
            tags,
          })
        ).unwrap()
      }

      await retrieveAccounts()

      dispatch(
        postSlackMessage({
          channel: SLACK_DATA_STATUS_CHANNEL_ID,
          text: `${user.email} saved Stripe secret key for account ${accountId}`,
        })
      )

      IntercomAPI('trackEvent', 'clicked-integration-connect-button', {
        email: user.email,
        channel: channelName,
      })

      toast.success(`Stripe account ${accountId} connected successfully`)
      closeModal()
    } catch (err) {
      console.error('Error saving Stripe key:', err)
      toast.error(`Failed to save Stripe key: ${err}`)
    }
  }

  return (
    <div className="tw-p-6">
      <h2 className="tw-text-xl tw-mb-2">Connect Your Stripe Account</h2>
      <div className="tw-text-slate-500 tw-mb-4">
        Enter your Stripe secret key to connect your account. You can find this in your{' '}
        <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="tw-text-blue-500 hover:tw-text-blue-700">
          Stripe Dashboard
        </a>{' '}
        under Developers &rarr; API keys.
      </div>

      {!ppKeys ? (
        <Spinner animation="border" variant="primary" />
      ) : (
        <form className="tw-w-full" onSubmit={handleSubmit(onSubmit)}>
          <div className="tw-rounded-xl tw-bg-slate-50 tw-p-3 tw-mb-4">
            <div className="tw-mb-3">
              <MyInput
                register={register}
                name="secretKey"
                label="Secret Key"
                type="password"
                rules={{
                  required: 'Secret key is required',
                  pattern: {
                    value: /^(sk_live_|sk_test_|rk_live_|rk_test_)/,
                    message: 'Must be a valid Stripe secret key (starts with sk_live_, sk_test_, rk_live_, or rk_test_)',
                  },
                }}
                errors={errors}
                placeholder="sk_live_..."
              />

              {hasExistingKey && (
                <div className="tw-text-slate-500 tw-text-sm tw-mt-2">
                  A secret key is already connected. Saving replaces it.
                </div>
              )}
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-justify-end tw-space-x-2">
            <Button variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || verifying}>
              {verifying ? (
                <>
                  <Spinner animation="border" size="sm" className="tw-mr-2" />
                  Verifying...
                </>
              ) : (
                'Save Stripe Connection'
              )}
            </Button>
          </div>
          {errors?.secretKey && (
            <div className="tw-flex tw-justify-end tw-mt-2">
              <div className="tw-text-red-500 tw-text-sm">{(errors.secretKey as any)?.message}</div>
            </div>
          )}
        </form>
      )}
    </div>
  )
}
